import { businessTimeZone, localBusinessTimeToIso } from "./availability-time";

export type ScheduleView = "day" | "week" | "month" | "team";

export function todayKey() {
  return businessDateKey(new Date());
}

export function businessDateKey(value: Date | string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: businessTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date(value))
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function calendarWindow(anchor: string, view: ScheduleView) {
  let start = anchor;
  let days = 1;
  if (view === "week") {
    start = addDays(anchor, -mondayOffset(anchor));
    days = 7;
  }
  if (view === "month") {
    const first = `${anchor.slice(0, 7)}-01`;
    start = addDays(first, -mondayOffset(first));
    days = 42;
  }
  return {
    startKey: start,
    endKey: addDays(start, days),
    start: localBusinessTimeToIso(`${start}T00:00`),
    end: localBusinessTimeToIso(`${addDays(start, days)}T00:00`),
    dates: Array.from({ length: days }, (_, index) => addDays(start, index)),
  };
}

export function shiftAnchor(anchor: string, view: ScheduleView, direction: -1 | 1) {
  if (view === "month") {
    const date = proxyDate(anchor);
    date.setUTCMonth(date.getUTCMonth() + direction, 1);
    return proxyKey(date);
  }
  return addDays(anchor, direction * (view === "week" ? 7 : 1));
}

export function formatRangeLabel(anchor: string, view: ScheduleView) {
  const window = calendarWindow(anchor, view);
  if (view === "month") return formatDate(anchor, { month: "long", year: "numeric" });
  if (view === "day" || view === "team") {
    return formatDate(anchor, { weekday: "long", month: "long", day: "numeric" });
  }
  const last = window.dates.at(-1)!;
  const startMonth = anchor.slice(0, 4) === last.slice(0, 4) ? undefined : "numeric";
  return `${formatDate(window.startKey, { month: "short", day: "numeric", year: startMonth })} – ${formatDate(last, { month: "short", day: "numeric", year: "numeric" })}`;
}

export function formatDate(
  key: string,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
) {
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(proxyDate(key));
}

export function formatTime(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: businessTimeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function mondayOffset(key: string) {
  return (proxyDate(key).getUTCDay() + 6) % 7;
}

function addDays(key: string, amount: number) {
  const date = proxyDate(key);
  date.setUTCDate(date.getUTCDate() + amount);
  return proxyKey(date);
}

function proxyDate(key: string) {
  return new Date(`${key}T12:00:00Z`);
}

function proxyKey(date: Date) {
  return date.toISOString().slice(0, 10);
}
